import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queueService, QueueStatusResponse, QueueStatsResponse, QueueJobsResponse } from '@services/queue.service'
import { toast } from 'react-hot-toast'

// Query keys
export const queueKeys = {
  all: ['queues'] as const,
  status: () => [...queueKeys.all, 'status'] as const,
  stats: (queueName: string) => [...queueKeys.all, 'stats', queueName] as const,
  jobs: (queueName: string, status?: string) => [...queueKeys.all, 'jobs', queueName, status] as const,
}

// Hook to fetch all queue metrics
export function useQueueStatus(refetchInterval: number = 5000) {
  return useQuery<QueueStatusResponse>({
    queryKey: queueKeys.status(),
    queryFn: () => queueService.getAllQueuesStatus(),
    refetchInterval,
    staleTime: 3000,
  })
}

// Hook to fetch specific queue statistics
export function useQueueStats(queueName: string, refetchInterval: number = 5000) {
  return useQuery<QueueStatsResponse>({
    queryKey: queueKeys.stats(queueName),
    queryFn: () => queueService.getQueueStats(queueName),
    refetchInterval,
    staleTime: 3000,
    enabled: !!queueName,
  })
}

// Hook to fetch jobs from a queue
export function useQueueJobs(
  queueName: string,
  status: string = 'waiting',
  limit: number = 50,
  refetchInterval: number = 10000
) {
  return useQuery<QueueJobsResponse>({
    queryKey: queueKeys.jobs(queueName, status),
    queryFn: () => queueService.getQueueJobs(queueName, status, limit),
    refetchInterval,
    staleTime: 5000,
    enabled: !!queueName,
  })
}

// Hook to pause a queue
export function usePauseQueue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (queueName: string) => queueService.pauseQueue(queueName),
    onSuccess: (data, queueName) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.status() })
      queryClient.invalidateQueries({ queryKey: queueKeys.stats(queueName) })
      toast.success(data.message || 'Queue paused successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to pause queue')
    },
  })
}

// Hook to resume a queue
export function useResumeQueue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (queueName: string) => queueService.resumeQueue(queueName),
    onSuccess: (data, queueName) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.status() })
      queryClient.invalidateQueries({ queryKey: queueKeys.stats(queueName) })
      toast.success(data.message || 'Queue resumed successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to resume queue')
    },
  })
}

// Hook to remove a job from queue
export function useRemoveJob() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      queueService.removeJob(queueName, jobId),
    onSuccess: (data, { queueName }) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.status() })
      queryClient.invalidateQueries({ queryKey: queueKeys.stats(queueName) })
      queryClient.invalidateQueries({ queryKey: queueKeys.jobs(queueName) })
      toast.success(data.message || 'Job removed successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to remove job')
    },
  })
}

// Hook to clean completed or failed jobs
export function useCleanQueue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      queueName,
      status,
      age,
    }: {
      queueName: string
      status: 'completed' | 'failed'
      age?: number
    }) => queueService.cleanQueue(queueName, status, age),
    onSuccess: (data, { queueName }) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.status() })
      queryClient.invalidateQueries({ queryKey: queueKeys.stats(queueName) })
      toast.success(`${data.jobsRemoved} jobs removed successfully`)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to clean queue')
    },
  })
}

// Hook to retry failed jobs
export function useRetryFailedJobs() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ queueName, limit }: { queueName: string; limit?: number }) =>
      queueService.retryFailedJobs(queueName, limit),
    onSuccess: (data, { queueName }) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.status() })
      queryClient.invalidateQueries({ queryKey: queueKeys.stats(queueName) })
      queryClient.invalidateQueries({ queryKey: queueKeys.jobs(queueName) })
      toast.success(`${data.retriedCount} jobs retried successfully`)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to retry jobs')
    },
  })
}
